import {parseISO} from "date-fns";
import React from "react";

import {EDocumentTypes} from "@/components/project/models";
import display from "@/services/display-name";
import http from "@/services/http";
import signal from "@/services/signal";
import {TABLE_PADDING} from "@/styles/_variables";
import {IAuditEntry} from "./models";

import T from "@translate/T";
import UserLink from "../building-blocks/UserLink";
import Card from "../design/Card";
import Time from "../design/Time";
import AuditAction from "../display-blocks/AuditAction";

export interface IAuditLogProps<TValue> {
    id?: string;
    endpoint: string;
    renderEntry(document: TValue, entry: IAuditEntry<TValue>): JSX.Element;
}

interface IAuditLogState<TValue> {
    entries: IAuditEntry<TValue>[];
}

class AuditLog<TValue = never> extends React.PureComponent<
    IAuditLogProps<TValue>,
    IAuditLogState<TValue>
> {
    public readonly state: IAuditLogState<TValue> = {entries: []};

    private readonly card = React.createRef<Card>();
    private unsubscribe?: () => void;

    private get linkWidth() {
        const body = this.card.current?.cardWidthNoBorder ?? 0;
        const padding = 2 * TABLE_PADDING;

        return body - padding;
    }

    public async componentDidMount() {
        await this.refresh();

        this.unsubscribe = signal.register({
            auditChanged: () => this.refresh(),
        });
    }

    public componentWillUnmount() {
        this.unsubscribe?.();
    }

    public render() {
        const {id} = this.props;
        const forceUpdate = () => this.forceUpdate();

        return (
            <Card
                ref={this.card}
                suffixId={id ? `${id}-audit-log` : "audit-log"}
                headerClassName="bg-secondary"
                title={<T>Activities</T>}
                triggerOnSizeChange={forceUpdate}
            >
                <table className="table table-sm table-striped table-borderless table-center">
                    <tbody>{this.renderContent()}</tbody>
                </table>
            </Card>
        );
    }

    private renderContent() {
        const entries = this.renderEntries();
        if (entries.length) {
            return entries;
        }

        return (
            <tr>
                <td>
                    <em id="no-activity" className="text-muted">
                        <T>No recent activity.</T>
                    </em>
                </td>
            </tr>
        );
    }

    private renderEntries() {
        const {renderEntry} = this.props;

        return this.state.entries
            .map((x) => {
                const item = renderEntry(x.document, x);
                if (item === null) {
                    return null;
                }

                return (
                    <tr key={x.id}>
                        <td id={"item-" + x.id}>{this.getAuditMessage(x)}</td>
                    </tr>
                );
            })
            .filter(Boolean);
    }

    private getAuditMessage(x: IAuditEntry<TValue>) {
        return (
            <React.Fragment>
                <UserLink
                    username={x.author.username}
                    displayName={display.getOnlyDefault(x.author)}
                    documentType={EDocumentTypes.User}
                />{" "}
                <AuditAction value={x.action} />{" "}
                <span
                    className="text-dot-overflow span-outside-link"
                    style={{maxWidth: this.linkWidth}}
                >
                    {this.props.renderEntry(x.document, x)}
                </span>
                <br />
                <Time value={parseISO(x.time)} />
            </React.Fragment>
        );
    }

    private async refresh() {
        try {
            const response = await http
                .get(this.props.endpoint)
                .json<IAuditEntry<TValue>[]>();

            this.setState({entries: response});
        } catch {
            this.setState({entries: []});
        }
    }
}

export default AuditLog;
